import { useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Skeleton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useAlert } from '../../core/AlertContext';
import { BlogComment, BlogReactionKind } from '../blogSchemas';
import { parseApiErrorMessage } from '../blogUtils';
import BlogCommentItem from './BlogCommentItem';
import { primaryButtonSx } from '../../core/pvtaStyles';

interface BlogCommentSectionProps {
    comments: BlogComment[];
    isLoading: boolean;
    isSubmitting: boolean;
    onCreate: (text: string, parentId?: number | null) => Promise<void>;
    onDelete: (commentId: number) => Promise<void>;
    onToggleReaction: (commentId: number, emoji: BlogReactionKind) => Promise<void>;
}

const BlogCommentSection: React.FC<BlogCommentSectionProps> = ({
    comments,
    isLoading,
    isSubmitting,
    onCreate,
    onDelete,
    onToggleReaction,
}) => {
    const { showAlert } = useAlert();
    const [draft, setDraft] = useState('');

    const handleCreate = async (): Promise<void> => {
        const text = draft.trim();
        if (!text) return;
        try {
            await onCreate(text, null);
            setDraft('');
        } catch (error) {
            showAlert({ severity: 'error', text: parseApiErrorMessage(error, 'Kommentar konnte nicht gespeichert werden.') });
        }
    };

    const handleCreateReply = async (parentId: number, text: string): Promise<void> => {
        try {
            await onCreate(text, parentId);
        } catch (error) {
            showAlert({ severity: 'error', text: parseApiErrorMessage(error, 'Antwort konnte nicht gespeichert werden.') });
            throw error;
        }
    };

    const handleDeleteComment = async (commentId: number): Promise<void> => {
        try {
            await onDelete(commentId);
            showAlert({ severity: 'success', text: 'Kommentar gelöscht.' });
        } catch (error) {
            showAlert({ severity: 'error', text: parseApiErrorMessage(error, 'Kommentar konnte nicht gelöscht werden.') });
        }
    };

    return (
        <Card variant="outlined">
            <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Kommentare
                </Typography>

                <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Kommentar schreiben"
                    value={draft}
                    onChange={({ target }) => setDraft(target.value)}
                    disabled={isSubmitting}
                />
                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                    <Button
                        size="small"
                        sx={{ ...primaryButtonSx }}
                        disabled={isSubmitting || !draft.trim()}
                        onClick={() => void handleCreate()}
                    >
                        Senden
                    </Button>
                </Stack>

                {isLoading && (
                    <Stack spacing={1.25} sx={{ py: 2 }}>
                        {[0, 1].map((item) => (
                            <Card key={item} variant="outlined">
                                <CardContent>
                                    <Stack direction="row" spacing={1} alignItems="flex-start">
                                        <Skeleton variant="circular" width={40} height={40} />
                                        <Box sx={{ flex: 1 }}>
                                            <Skeleton variant="text" width="35%" height={24} />
                                            <Skeleton variant="text" width="25%" height={18} />
                                            <Skeleton variant="text" width="95%" />
                                            <Skeleton variant="text" width="70%" />
                                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                                <Skeleton variant="rounded" width={64} height={28} />
                                                <Skeleton variant="rounded" width={64} height={28} />
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                )}

                {!isLoading && comments.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                        Noch keine Kommentare.
                    </Typography>
                )}

                <Stack spacing={0} sx={{ mt: 1.5 }}>
                    {comments.map((comment) => (
                        <BlogCommentItem
                            key={comment.id}
                            comment={comment}
                            isWorking={isSubmitting}
                            onCreateReply={handleCreateReply}
                            onDeleteComment={handleDeleteComment}
                            onToggleReaction={onToggleReaction}
                        />
                    ))}
                </Stack>
            </CardContent>
        </Card>
    );
};

export default BlogCommentSection;
